import type { AppShellPinButtonProps } from "../../../../../../shared/frontend/layout/AppShellPinButton";
import React from "react";
import { useI18n } from "../i18n/useI18n";
import { isPersonDetailPath, resolveRoutePinDisplay } from "../pins/pinnablePage";
import { routePinId } from "../pins/pinnedLinks";
import type { PinnedLink } from "../pins/pinnedLinks";
import { usePinnedLinks } from "../pins/usePinnedLinks";
import { normalizeMenuPath } from "./menu";
import type { MenuRoute } from "./menu";
import { useFeedback } from "../../../../../../shared/frontend/feedback/useFeedback";

/**
 * Pin-Umschalter für die aktuelle Seite — die EINE zentrale Stelle zum Anpinnen.
 *
 * Hat die Seite einen fachlichen Pin gemeldet (`useSetPagePin`, z. B. eine Personenakte mit
 * Name und Foto), gewinnt dieser. Sonst wird die Route über Menü und Seitenkataloge aufgelöst.
 */
export function useCurrentPagePinConfiguration(props: { menu: MenuRoute[]; path: string; pagePin: PinnedLink | null }): AppShellPinButtonProps | undefined {
  const { menu, path, pagePin } = props;
  const { text, tr } = useI18n();
  const feedback = useFeedback();
  const { isPinned, togglePin } = usePinnedLinks();

  const normalizedPath = normalizeMenuPath(path);

  const routeDisplay = React.useMemo(
    () => resolveRoutePinDisplay({ menu, path: normalizedPath, tr }),
    [menu, normalizedPath, tr]
  );

  const routePin: PinnedLink = React.useMemo(
    () => ({ id: routePinId(normalizedPath), kind: "route", path: normalizedPath }),
    [normalizedPath]
  );

  const pin = pagePin ?? routePin;

  // Auf einer Personenakte ohne gemeldeten Pin sind die Personendaten noch nicht geladen.
  // Dann lieber kurz keinen Knopf zeigen als einen namenlosen Routen-Pin anzubieten.
  if (!pagePin && (isPersonDetailPath(normalizedPath) || !routeDisplay)) return undefined;

  const pinned = isPinned(pin.id);
  const label = pinned ? text("Remove pin") : text("Pin");

  return (
    ({
pinned: pinned,
      label: label,
      onToggle: () => {
        if (!togglePin(pin)) feedback.warning(text("Maximum number of pinned pages reached"));
      }
})
  );
}
